import LegalPersonFields from "./components/LegalPersonFields";
import PhysicalPersonFields from "./components/PhysicalPersonFields";
import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Container,
  Form,
  Modal,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import ModalHeader from "@dhn/components/molecules/modal-header";

import LoadingDhn from "../../@dhn/components/Loading/LoadingDhn";
import "../MedicalPreregistrationPage.scss";
import useAction from "./hooks/use-action";
import {
  getCompanyDisplayName,
} from "../../@dhn/shared/company-info";
import CompanyLogo from "../../@dhn/components/CompanyLogo";

import DocumentsGalleryModal from "./components/DocumentsGalleryModal";
import DocumentsStep from "./components/DocumentsStep";
import SpecialityEvidence from "./components/SpecialityEvidence";
import FormFooter from "./components/FormFooter";
import IdentityValidationStep from "./components/IdentityValidationStep";
import LegalRequirementsStep from "./components/LegalRequirementsStep";

const MedicalPreregistrationPage = () => {
  const { state, actions } = useAction();
  const [companyInfo, setCompanyInfo] = useState(null);
  const [isCompanyInfoLoading, setIsCompanyInfoLoading] = useState(true);

  const {
    form,
    documents,
    supportingDocuments,
    specialityEvidenceDocuments,
    galleryDocuments,
    isSubmitting,
    isValidatingIdentity,
    activeDocument,
    activeDocumentIndex,
    isDocumentsModalOpen,
    result,
    catalogError,
    provinces,
    specialities,
    isLoadingSpecialities,
    physicalMunicipalities,
    physicalSectors,
    businessMunicipalities,
    businessSectors,
    hasSelectedPersonType,
    hasSelectedMedicalType,
    legalUsername,
    legalDocument,
    inlineErrors,
    submitBlockers,
    isCreateDisabled,
    isValidateDisabled,
    requiredDocumentRequirements,
    requiredDocumentsCount,
    documentsTotalSize,
    documentRequirementsCompleted,
    canShowLegalRequirements,
    identityValidation,
    representativeValidation,
    isRepresentativeUserModalOpen,
    isSuccessModalOpen,
  } = state;

  const {
    onChange,
    onDocumentNumberChange,
    onFilesChange,
    openSupportingDocumentsModal,
    openSpecialityEvidenceModal,
    closeDocumentsModal,
    goToPreviousDocument,
    goToNextDocument,
    removeDocument,
    removeGalleryDocumentAt,
    setActiveDocumentIndex,
    handleValidateIdentity,
    handleValidateRepresentativeIdentity,
    handleSubmit,
    onRepresentativeDocumentNumberChange,
    onRequirementFileChange,
    onAddSpeciality,
    onRemoveSpeciality,
    removeSpecialityEvidenceById,
    resetForm,
    startAnotherRequest,
    closeRepresentativeUserModal,
  } = actions;

  const showInlineError = (field) =>
    inlineErrors[field] ? (
      <small className="text-danger d-block mt-1">{inlineErrors[field]}</small>
    ) : null;

  const primaryDocumentLabel =
    form.documentType === "RNC" ? "RNC" : "Cédula";
  const primaryDocumentValue = form.documentNumber;

  const representativeValidateButtonIcon = isValidatingIdentity
    ? "fa-spinner fa-spin"
    : representativeValidation.status === "validated"
      ? "fa-check-circle"
      : representativeValidation.status === "conflict"
        ? "fa-exclamation-triangle"
        : representativeValidation.status === "error"
          ? "fa-refresh"
          : "fa-search";

  const representativeValidateButtonLabel = isValidatingIdentity
    ? "Validando..."
    : representativeValidation.status === "validated"
      ? "Validar de nuevo"
      : representativeValidation.status === "conflict"
        ? "Validar de nuevo"
        : representativeValidation.status === "error"
          ? "Reintentar"
          : "Validar";
  const representativeLockedFields = representativeValidation.lockedFields || {};
  const isRepresentativeFieldLocked = (fieldName) =>
    Boolean(representativeLockedFields[fieldName]);
  const existingRepresentativeUser = representativeValidation.existingUser || {};
  const existingRepresentativeUserRows = [
    ["Nombre", existingRepresentativeUser.firstName, "fa-user-o"],
    ["Apellido", existingRepresentativeUser.lastName, "fa-id-badge"],
    [
      "Documento",
      existingRepresentativeUser.identification || form.repDocumentNumber,
      "fa-address-card-o",
    ],
    ["Correo", existingRepresentativeUser.email, "fa-envelope-o"],
    ["Usuario", existingRepresentativeUser.username, "fa-shield"],
  ].filter(([, value]) => Boolean((value || "").toString().trim()));
  const companyName = getCompanyDisplayName(companyInfo, "DHN");

  useEffect(() => {
    let isMounted = true;
    const apiBaseUrl = (process.env.REACT_APP_APOLLO_CLIENT_URI || "").replace(/\/$/, "");

    if (!apiBaseUrl) {
      setIsCompanyInfoLoading(false);
      return undefined;
    }

    fetch(`${apiBaseUrl}/public/legal-entity`)
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (isMounted) setCompanyInfo(data || null);
      })
      .catch(() => {
        if (isMounted) setCompanyInfo(null);
      })
      .finally(() => {
        if (isMounted) setIsCompanyInfoLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <Container
      fluid
      className={`medical-prereg-shell ${isSuccessModalOpen ? "is-success-mode" : ""
        }`}
    >
      <LoadingDhn isOpen={isSubmitting} isDescription={false} />
      {!isSuccessModalOpen && (
        <Container className="medical-prereg-wrapper">
          <Card className="medical-prereg-card">
            <CardBody>
              <div className="medical-prereg-header">
                <CompanyLogo
                  companyInfo={companyInfo}
                  loading={isCompanyInfoLoading}
                  alt={companyName}
                  className="medical-prereg-logo"
                  loaderClassName="medical-prereg-logo"
                  loaderStyle={{ fontSize: 28, color: "#1f7db7" }}
                />
                <h3 className="mb-2">Registro de Usuario</h3>
                <p className="text-muted mb-0">
                  Bienvenido a DHN, llene el formulario para solicitar su
                  acceso al sistema.
                </p>
              </div>

              <Form onSubmit={handleSubmit} className="medical-prereg-form">
                {catalogError && <div className="alert alert-danger" role="alert">{catalogError}</div>}

                <IdentityValidationStep
                  form={form}
                  inlineErrors={inlineErrors}
                  identityValidation={identityValidation}
                  isValidatingIdentity={isValidatingIdentity}
                  isValidateDisabled={isValidateDisabled}
                  hasSelectedPersonType={hasSelectedPersonType}
                  hasSelectedMedicalType={hasSelectedMedicalType}
                  onChange={onChange}
                  onDocumentNumberChange={onDocumentNumberChange}
                  handleValidateIdentity={handleValidateIdentity}
                  showInlineError={showInlineError}
                />

                {hasSelectedPersonType &&
                  hasSelectedMedicalType &&
                  identityValidation.status === "validated" && (
                    <>
                      <hr />
                      <div className="prereg-step-header">
                        <span className="prereg-step-index">Paso 2</span>
                        <h6>Datos del Solicitante</h6>
                      </div>

                      {form.personType === "FISICA" && (
                        <PhysicalPersonFields
                          form={form}
                          isSubmitting={isSubmitting}
                          provinces={provinces}
                          physicalMunicipalities={physicalMunicipalities}
                          physicalSectors={physicalSectors}
                          legalUsername={legalUsername}
                          inlineErrors={inlineErrors}
                          identityValidation={identityValidation}
                          onChange={onChange}
                          showInlineError={showInlineError}
                          primaryDocumentLabel={primaryDocumentLabel}
                          primaryDocumentValue={primaryDocumentValue}
                        />
                      )}

                      {form.personType === "MORAL" && (
                        <LegalPersonFields
                          form={form}
                          isSubmitting={isSubmitting}
                          isValidatingIdentity={isValidatingIdentity}
                          provinces={provinces}
                          businessMunicipalities={businessMunicipalities}
                          businessSectors={businessSectors}
                          legalUsername={legalUsername}
                          inlineErrors={inlineErrors}
                          identityValidation={identityValidation}
                          representativeValidation={representativeValidation}
                          onChange={onChange}
                          handleValidateRepresentativeIdentity={handleValidateRepresentativeIdentity}
                          onRepresentativeDocumentNumberChange={onRepresentativeDocumentNumberChange}
                          showInlineError={showInlineError}
                          representativeValidateButtonIcon={representativeValidateButtonIcon}
                          representativeValidateButtonLabel={representativeValidateButtonLabel}
                          isRepresentativeFieldLocked={isRepresentativeFieldLocked}
                        />
                      )}

                      {form.isMedical && (
                        <div className="fade-in-up">
                          <SpecialityEvidence
                            specialities={form.specialities}
                            specialityOptions={specialities}
                            documents={specialityEvidenceDocuments}
                            isSubmitting={isSubmitting}
                            isLoadingSpecialities={isLoadingSpecialities}
                            onAddSpeciality={onAddSpeciality}
                            onRemoveSpeciality={onRemoveSpeciality}
                            removeSpecialityEvidenceById={removeSpecialityEvidenceById}
                            onDraftStateChange={(pending) =>
                              onChange("specialityDraftPending", pending)
                            }
                            onFilesChange={onFilesChange}
                            openDocumentsModal={openSpecialityEvidenceModal}
                            inlineErrors={inlineErrors}
                          />
                        </div>
                      )}

                      <DocumentsStep
                        isSubmitting={isSubmitting}
                        form={form}
                        documents={supportingDocuments}
                        documentsTotalSize={documentsTotalSize}
                        requiredDocumentRequirements={requiredDocumentRequirements}
                        documentRequirementsCompleted={documentRequirementsCompleted}
                        onFilesChange={onFilesChange}
                        openDocumentsModal={openSupportingDocumentsModal}
                        removeDocument={removeDocument}
                        onRequirementFileChange={onRequirementFileChange}
                        showInlineError={showInlineError}
                      />

                      <LegalRequirementsStep
                        canShowLegalRequirements={canShowLegalRequirements}
                        documentsLength={documents.length}
                        requiredDocumentsCount={requiredDocumentsCount}
                        legalDocument={legalDocument}
                        form={form}
                        onChange={onChange}
                        showInlineError={showInlineError}
                      />
                    </>
                  )}

                <FormFooter
                  result={result}
                  submitBlockers={submitBlockers}
                  resetForm={resetForm}
                  isSubmitting={isSubmitting}
                  isValidatingIdentity={isValidatingIdentity}
                  isCreateDisabled={isCreateDisabled}
                />
              </Form>

              <DocumentsGalleryModal
                isOpen={isDocumentsModalOpen}
                closeDocumentsModal={closeDocumentsModal}
                activeDocument={activeDocument}
                activeDocumentIndex={activeDocumentIndex}
                documents={galleryDocuments}
                removeDocumentAt={removeGalleryDocumentAt}
                goToPreviousDocument={goToPreviousDocument}
                goToNextDocument={goToNextDocument}
                setActiveDocumentIndex={setActiveDocumentIndex}
              />

              <Modal
                isOpen={isRepresentativeUserModalOpen}
                toggle={closeRepresentativeUserModal}
                centered
              >
                <ModalHeader
                  toggle={closeRepresentativeUserModal}
                  iconClass="fa-user-circle-o"
                  description="Representante legal existente"
                  subtitle="Revise los datos del usuario ya registrado."
                />
                <ModalBody>
                  <p className="representative-user-modal-copy">
                    Lo tenemos creado como usuario con los siguientes datos:
                  </p>
                  <div className="representative-user-summary">
                    {existingRepresentativeUserRows.map(([label, value, icon]) => (
                      <div className="representative-user-summary-row" key={label}>
                        <span className="representative-user-row-label">
                          <i className={`fa ${icon}`} aria-hidden="true"></i>
                          <strong>{label}:</strong>
                        </span>
                        <span>{value}</span>
                      </div>
                    ))}
                  </div>
                  <div className="representative-user-note">
                    <i className="fa fa-lock" aria-hidden="true"></i>
                    <p className="mb-0">
                      Debemos mantener el mismo usuario por su seguridad también en esta cuenta.
                      El costo de la licencia seguirá siendo el mismo contratado hasta su vigencia actual.
                    </p>
                  </div>
                </ModalBody>
                <ModalFooter>
                  <Button
                    color="primary"
                    onClick={closeRepresentativeUserModal}
                  >
                    <i className="fa fa-check mr-1" aria-hidden="true"></i>
                    Entendido
                  </Button>
                </ModalFooter>
              </Modal>
            </CardBody>
          </Card>
        </Container>
      )}

      <Modal
        isOpen={isSuccessModalOpen}
        centered
        backdrop="static"
        backdropClassName="prereg-success-backdrop"
        keyboard={false}
        className="prereg-success-modal"
      >
        <ModalBody>
          <div className="prereg-success-hero">
            <span className="prereg-success-pulse" aria-hidden="true"></span>
            <div className="prereg-success-logo-wrap">
              <CompanyLogo
                companyInfo={companyInfo}
                loading={isCompanyInfoLoading}
                alt={companyName}
                className="prereg-success-logo"
                loaderClassName="prereg-success-logo"
                loaderStyle={{ fontSize: 24, color: "#1f7db7" }}
              />
            </div>
            <div className="prereg-success-check">
              <i className="fa fa-check" aria-hidden="true"></i>
            </div>
            <h4>Solicitud enviada</h4>
            <p>
              Recibimos su solicitud. Administración revisará la información y
              le notificará el siguiente paso.
            </p>
          </div>
          <div className="prereg-success-question">
            <i className="fa fa-refresh" aria-hidden="true"></i>
            <span>¿Desea registrar otra solicitud ahora?</span>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            className="prereg-success-action"
            onClick={startAnotherRequest}
          >
            <i className="fa fa-plus-circle mr-2" aria-hidden="true"></i>
            Nueva solicitud
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default MedicalPreregistrationPage;
